import { ChangeDetectionStrategy, Component, inject, output, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
	MatDateRangeInput,
	MatDateRangePicker,
	MatDatepickerToggle,
	MatEndDate,
	MatStartDate,
} from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { GenericMatAutocompleteComponent } from '@/common/generic-mat-autocomplete/generic-mat-autocomplete.component';
import { environment } from 'src/environments/environment';
import { Contractor, STATUS_VINDICATION_FILTERS } from '../../vindication.model';
import { Result } from '@/reservation/reservation.model';
import { DatePipe } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';

interface InvoiceFiltersFormValue {
	nip?: string;
	contractorId?: number;
	number?: string;
	paymentDeadlineFrom?: Date | null;
	paymentDeadlineTo?: Date | null;
	status?: string;
	navigatorId?: number;
	ourCompany?: string;
	daysAfterDeadlineMin?: number;
	daysAfterDeadlineMax?: number;
	deferredPaymentDaysMin?: number;
	deferredPaymentDaysMax?: number;
	carrierReference?: string;
}

const FILTER_PARAM_MAP: Record<keyof InvoiceFiltersFormValue, string> = {
	nip: 'nip',
	contractorId: 'contractor',
	number: 'number',
	paymentDeadlineFrom: 'payment_deadline_after',
	paymentDeadlineTo: 'payment_deadline_before',
	status: 'status',
	navigatorId: 'navigator_id',
	ourCompany: 'our_company',
	daysAfterDeadlineMin: 'days_after_deadline_min',
	daysAfterDeadlineMax: 'days_after_deadline_max',
	deferredPaymentDaysMin: 'deferred_payment_days_min',
	deferredPaymentDaysMax: 'deferred_payment_days_max',
	carrierReference: 'carrier_reference',
};

@Component({
	selector: 'app-invoice-filters',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatInputModule,
		MatSelectModule,
		MatButtonModule,
		MatDateRangeInput,
		MatDateRangePicker,
		MatDatepickerToggle,
		MatStartDate,
		MatEndDate,
		GenericMatAutocompleteComponent,
	],
	templateUrl: './invoice-filters.component.html',
	styleUrl: './invoice-filters.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
})
export class InvoiceFiltersComponent implements OnInit {
	readonly statusVindicationFilters = STATUS_VINDICATION_FILTERS;

	readonly showFiltersBtnClick = output<string>();

	readonly contractorsApiUrl = `${environment.debtCollectionUrl}/contractors/`;

	readonly invoiceFilterForm = inject(FormBuilder).group({
		nip: ['', [Validators.pattern(/^\d{10}$|^\d{3}[-\s]?\d{3}[-\s]?\d{2}[-\s]?\d{2}$/)]],
		contractorId: [null],
		number: [''],
		paymentDeadlineFrom: [null],
		paymentDeadlineTo: [null],
		status: [null],
		navigatorId: [null],
		ourCompany: [null],
		daysAfterDeadlineMin: [null],
		daysAfterDeadlineMax: [null],
		deferredPaymentDaysMin: [null],
		deferredPaymentDaysMax: [null],
		carrierReference: [''],
	});

	private readonly datePipe = inject(DatePipe);
	private readonly dateAdapter = inject(DateAdapter);

	contractorsResponseMapper = (response: Result<Contractor>): Contractor[] => {
		return response?.results ?? [];
	};

	ngOnInit(): void {
		this.dateAdapter.setLocale('pl-PL');
	}

	showInvoiceFilters(): void {
		this.invoiceFilterForm.markAllAsTouched();
		if (this.invoiceFilterForm.invalid) {
			return;
		}
		const filtersParams = this.buildFiltersParams(this.invoiceFilterForm.value);
		this.showFiltersBtnClick.emit(filtersParams);
	}

	onEnterKey(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.showInvoiceFilters();
		}
	}

	resetFilters(): void {
		this.invoiceFilterForm.reset();
	}

	private formatDateValue(date: Date): string | null {
		return this.datePipe.transform(date, 'yyyy-MM-dd');
	}

	private formatFilterValue(value: Date | string | number): string | null {
		if (value instanceof Date) {
			return this.formatDateValue(value);
		}
		if (typeof value === 'string' || typeof value === 'number') {
			return String(value);
		}
		return null;
	}

	private buildFiltersParams(filters: InvoiceFiltersFormValue): string {
		const params = new URLSearchParams();

		Object.entries(filters).forEach(([key, value]) => {
			if (value === null || value === '') {
				return;
			}

			const paramName = FILTER_PARAM_MAP[key as keyof InvoiceFiltersFormValue];
			if (!paramName) {
				return;
			}

			const formattedValue = this.formatFilterValue(value);
			if (formattedValue) {
				params.append(paramName, formattedValue);
			}
		});

		const paramsString = params.toString();
		return paramsString ? `&${paramsString}` : '';
	}
}
